import * as React from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {colors} from 'shells/colors/colors';
import {CardWidgetEmptyState} from 'shells/empty-states/card-widget-empty-state';
import {LoadingIcon, PencilToPaperIcon} from 'shells/icon';

import type {NutshellState} from '../../../../../store';
import {fieldSortFunction, getTitle} from '../../../../../ui/core/nutshell-fields/helpers';
import type {FormSchemaProperty} from '../../../../../ui/core/nutshell-fields/types';
import {fetchSchemaAction} from '../../../../../ui/forms/forms-actions';
import {getFormSchemaFields} from '../../../../../ui/forms/forms-selectors';

import {ScrollableContainerWithShadow} from '../layout/scrollable-container-with-shadow/scrollable-container-with-shadow';
import {SearchBar} from '../search-bar/search-bar';

import {CustomFieldCard} from './custom-field-card';
import {getFieldKey, getFilteredCustomFields} from './helpers';

import styles from './custom-field-picker-popover.module.css';

const PANEL_HEIGHT = 300;
const SEARCH_HEADER_VERTICAL_PADDING = 16 + 12;
const SEARCH_BAR_HEIGHT = 35;
const LIST_MAX_HEIGHT = PANEL_HEIGHT - SEARCH_HEADER_VERTICAL_PADDING - SEARCH_BAR_HEIGHT;

interface Props {
    onSelect: (field: FormSchemaProperty) => void;
}

export function CustomFieldPickerPopover(props: Props) {
    const [searchValue, setSearchValue] = React.useState<string>('');

    const dispatch = useDispatch();
    const schemaFields = useSelector((state: NutshellState) => getFormSchemaFields(state));

    React.useEffect(() => {
        // @ts-expect-error
        dispatch(fetchSchemaAction());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isLoading = schemaFields === null;

    const filteredFields = getFilteredCustomFields(schemaFields ?? []).sort((a, b) =>
        fieldSortFunction(
            {isDisabled: false, entityType: a.entityType, label: a.label},
            {isDisabled: false, entityType: b.entityType, label: b.label}
        )
    );

    const normalizedSearchValue = searchValue.trim().toLowerCase();
    const searchResults = !normalizedSearchValue
        ? filteredFields
        : filteredFields.filter((field) =>
              getTitle(field).toLowerCase().includes(normalizedSearchValue)
          );

    const handleAdd = (selectedField: FormSchemaProperty) => {
        if (!selectedField.mergeTagName) {
            return;
        }

        props.onSelect(selectedField);
    };

    const renderListContent = () => {
        if (isLoading) {
            return (
                <div className='full-height flex justify-center align-center'>
                    <LoadingIcon size={40} fill={colors.greyLt} />
                </div>
            );
        }

        if (searchResults.length === 0) {
            return (
                <div className='full-height flex justify-center align-center'>
                    <CardWidgetEmptyState
                        text='No custom fields found'
                        icon={PencilToPaperIcon}
                        iconSize={24}
                    />
                </div>
            );
        }

        return (
            <ScrollableContainerWithShadow direction='vertical' maxContainerSize={LIST_MAX_HEIGHT}>
                <div className='flex flex-dir-col gap-4 pb-8 px-16 flex-grow min-height-0'>
                    {searchResults.map((field) => (
                        <CustomFieldCard
                            key={getFieldKey(field)}
                            field={field}
                            isSelected={false}
                            onClick={() => handleAdd(field)}
                        />
                    ))}
                </div>
            </ScrollableContainerWithShadow>
        );
    };

    return (
        <div className={styles.panel}>
            <div className='px-16 pt-16 pb-12'>
                <SearchBar
                    value={searchValue}
                    onChange={setSearchValue}
                    isClearable={true}
                    hasBorder={true}
                    placeholder='Search for a custom field'
                />
            </div>
            {renderListContent()}
        </div>
    );
}
